import Button from '@/components/Buttons/Button';
import { MaskedIcon } from '@/components/MaskedIcon/MaskedIcon';
import SingUpModal from '@/components/Modals/SingUpModal';
import { toast } from '@/components/toast';
import UploadFile from '@/images/UploadFile.svg';
import UploadPurple from '@/images/UploadPurple.svg';
import { ErrorBanner } from '@/pages/agents/components/ErrorBanner';
import { MAX_FILE_SIZE, MAX_FILES_UPLOAD } from '@/pages/agents/constants';
import { DocumentLanguageGuide } from '@/pages/agents/pages/editor/components/DocumentLanguageGuide';
import { useAnalytics } from '@/services/analytics';
import { useAuth } from '@/services/auth';
import { handleFileUploadAndValidation, UploadingFile } from '@/state/agent';
import { useAgentEditorStore } from '@/state/root';
import theme from '@/theme';
import { AgentCreateTabType, AgentGrounding, AgentKnowledgeSource } from '@d-id/types';
import styled from '@emotion/styled';
import { produce } from 'immer';
import { toJS } from 'mobx';
import { observer } from 'mobx-react-lite';
import { useCallback, useMemo, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import UploadedList from './UploadedList';
import { UploadGuideline } from './UploadGuideline';

const DropArea = styled.div<{ disabled?: boolean; isDragActive?: boolean }>`
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: center;
    text-align: center;

    height: 72px;

    padding: 8px 12px;
    margin-bottom: 8px;

    background: ${theme.colors.black[50]};
    border-radius: 12px;
    border: 1px dashed ${theme.colors.secondary[200]};
    outline: none;

    cursor: pointer;
    transition: background 0.3s ease;

    &:hover {
        background: ${theme.colors.black[100]};
    }

    ${({ disabled }) => disabled && 'opacity: 0.5;'}
`;

const UploadIconContainer = styled(MaskedIcon)`
    margin-right: auto;
    margin-left: auto;
    display: flex;
    color: ${theme.colors.secondary[400]};
`;

const DropAreaText = styled.text`
    color: ${theme.colors.secondary[400]};
    font-size: 14px;
    font-style: normal;
    font-weight: 500;
    line-height: 20px;
    letter-spacing: -0.28px;
`;

const UploadDiv = styled.span`
    margin: 0;
`;

const DropAreaContainer = styled.div`
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: space-between;

    width: 100%;
`;

const DropAreaContainerStart = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;

    gap: 12px;
`;

const DropAreaContainerEnd = styled.div`
    display: flex;
    align-items: center;
    justify-content: flex-end;
    flex-grow: 1;
`;

const StyledUploadButton = styled(Button)`
    padding: 0;
    height: 32px;
`;

const DropAreaUploadButtonContainer = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;

    gap: 6px;
    padding: 7px 8px;
    width: 88px;
    height: 32px;

    span {
        font-size: 14px;
        font-style: normal;
        font-weight: 600;
        line-height: 20px;
    }
`;

const InfoText = styled.p`
    width: 100%;

    margin-bottom: 16px;

    color: ${theme.colors.secondary.main};
    font-size: 12px;
    font-style: normal;
    font-weight: 400;
    line-height: 16px;
    letter-spacing: 0.17px;
`;

const SubParagraphTitleText = ({ isDragActive }: { isDragActive: boolean }) => {
    // TODO: re-enable this popover when we find a good way to handle the styling of it on the disabled state

    // const { isPopoverOpen, refs, floatingStyles, getFloatingProps, getReferenceProps, arrowProps } = usePopover({
    //     type: 'hover',
    //     options: { placement: 'top' },
    //     offsetOptions: { mainAxis: 20 },
    // });

    return isDragActive ? (
        <>
            <DropAreaText>Drop to upload</DropAreaText>
        </>
    ) : (
        <>
            <DropAreaContainer>
                <DropAreaContainerStart>
                    <UploadIconContainer src={UploadFile} color={theme.colors.secondary[400]} size="24px" />
                    <DropAreaText>Drop files here to upload</DropAreaText>
                </DropAreaContainerStart>
                <DropAreaContainerEnd>
                    <StyledUploadButton color="secondary">
                        {/* <DropAreaUploadButtonContainer ref={refs.setReference} {...getReferenceProps()}> */}

                        <DropAreaUploadButtonContainer>
                            <UploadIconContainer src={UploadPurple} color={theme.colors.white} size="16px" />
                            <span>Upload</span>
                        </DropAreaUploadButtonContainer>
                    </StyledUploadButton>
                </DropAreaContainerEnd>
            </DropAreaContainer>
            {/* {disabled && isPopoverOpen && (
                <PopoverRoot ref={refs.setFloating} style={floatingStyles} {...getFloatingProps()}>
                    <FloatingArrow {...arrowProps} />
                    <PopoverContentContainer>You can upload up to {MAX_FILES_UPLOAD} files</PopoverContentContainer>
                </PopoverRoot>
            )} */}
        </>
    );
};

export const UploadFiles = observer(() => {
    const agentEditorStore = useAgentEditorStore();

    const mixpanel = useAnalytics();
    const { isAnonymous, loginWithRedirect } = useAuth();
    const [totalUploadedFiles, setTotalUploadedFiles] = useState(0);
    const [isOpenModal, setModalState] = useState(false);
    const agent = agentEditorStore.inputAgent;
    const agentId = agent.id;

    const onDrop = useCallback(
        async (files: File[]) => {
            if (isAnonymous) {
                setModalState(true);
                return;
            }

            mixpanel?.track('agent-document-uploading', { event: 'uploading', files: files.length, agentId });
            const totalFiles = totalUploadedFiles + files.length;
            if (totalFiles > MAX_FILES_UPLOAD) {
                toast.error({
                    title: `Cannot upload more than ${MAX_FILES_UPLOAD} documents`,
                    toastId: 'agent-generate-too-many-documents',
                });
                return;
            } else if (files.filter((file: any) => file.size > MAX_FILE_SIZE).length > 0) {
                toast.error({
                    title: `Cannot upload documents larger than 20MB`,
                    toastId: 'agent-generate-too-many-documents',
                });
                return;
            }
            setTotalUploadedFiles(totalFiles);
            agentEditorStore.setErrors(errors => ({ ...errors, missingFilesError: null }));
            agentEditorStore.setErrorTabs(errorTabs => errorTabs.filter(tab => tab !== AgentCreateTabType.KnowledgeBase));
            await handleFileUploadAndValidation(files, agentEditorStore.setUploadingDocumentFiles, mixpanel, agentId);
            agentEditorStore.setLLM(
                produce(toJS(agentEditorStore.llm), llm => {
                    (llm.prompt_customization ??= {}).knowledge_source ??= AgentKnowledgeSource.Documents;
                })
            );
        },
        [mixpanel, loginWithRedirect, handleFileUploadAndValidation, totalUploadedFiles, agentId, isAnonymous]
    );

    const onCloseModal = () => setModalState(false);

    const handleClick = useCallback(() => {
        if (isAnonymous) {
            setModalState(true);
        }
    }, [isAnonymous]);

    const isUploadDisabled = useMemo(
        () => agentEditorStore.uploadingDocumentFiles.length >= MAX_FILES_UPLOAD,
        [agentEditorStore.uploadingDocumentFiles.length]
    );
    const isKnowledgeDisabled = useMemo(() => agentEditorStore.llm.template === AgentGrounding.Ungrounded, [agentEditorStore.llm.template]);
    const isDropAreaDisabled = useMemo(
        () => isUploadDisabled || isKnowledgeDisabled || agentEditorStore.isUpdatingKnowledge,
        [isUploadDisabled, isKnowledgeDisabled, agentEditorStore.isUpdatingKnowledge]
    );

    const { getRootProps, getInputProps, isDragActive } = useDropzone({
        onDrop,
        accept: {
            'application/pdf': ['.pdf'],
            'text/plain': ['.txt'],
            'application/vnd.ms-powerpoint': ['.ppt', '.pptx'],
        },
        onDropRejected: rejectedFiles => {
            const unsupportedTypes = [...new Set(rejectedFiles.map(rejectedFile => rejectedFile.file.type))];
            toast.error({
                title: `Unsupported file types: ${unsupportedTypes.join(', ')}`,
                toastId: `agent-generate-invalid-document-type}`,
            });
        },
        disabled: isDropAreaDisabled,
    });

    const handleDeleteFile = useCallback(
        (uploadingFile: UploadingFile) => {
            agentEditorStore.setUploadingDocumentFiles(prevFiles => prevFiles.filter(item => item.file !== uploadingFile.file));
            setTotalUploadedFiles(prevTotalUploadedFiles => prevTotalUploadedFiles - 1);
            agentEditorStore.setDeletedDocumentFiles(prevFiles => [...prevFiles, uploadingFile]);
        },
        [agentEditorStore.setUploadingDocumentFiles, setTotalUploadedFiles, agentEditorStore.setDeletedDocumentFiles]
    );

    return (
        <UploadDiv>
            {agentEditorStore.errors.missingFilesError && <ErrorBanner description={agentEditorStore.errors.missingFilesError} />}
            <DocumentLanguageGuide />

            {isAnonymous ? (
                <DropArea
                    isDragActive={isDragActive}
                    {...getRootProps({ className: 'dropzone' })}
                    disabled={isDropAreaDisabled}
                    onClick={handleClick}>
                    <SubParagraphTitleText isDragActive={isDragActive} />
                </DropArea>
            ) : (
                <DropArea {...getRootProps({ className: 'dropzone' })} disabled={isDropAreaDisabled} isDragActive={isDragActive}>
                    <input {...getInputProps()} />
                    <SubParagraphTitleText isDragActive={isDragActive} />
                </DropArea>
            )}

            <InfoText>
                Up to {MAX_FILES_UPLOAD} files, each up to 20MB, PDF, TXT, PPTX. <UploadGuideline maxFilesUpload={MAX_FILES_UPLOAD} />
            </InfoText>
            <UploadedList
                uploadingFiles={agentEditorStore.uploadingDocumentFiles}
                handleDeleteFile={handleDeleteFile}
                disableDelete={agentEditorStore.isUpdatingKnowledge}
            />
            <SingUpModal isOpen={isOpenModal} onClose={onCloseModal} from="agent-document-create" />
        </UploadDiv>
    );
});
